import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  updateContact,
  updateContactUserPortal,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import {
  Biography,
  Birthday,
  ContactName,
  ContactRelation,
  EmailAddress,
  Event,
  ImClient,
  Membership,
  NickName,
  Person,
  PersonAddress,
  PersonMetadata,
  PersonOrg,
  PhoneNumber,
  Url,
  UserDefined,
} from '../../../../../types';
import GeneralDialog from '../../../../shared/general-dialog';
import ContactAccordionSection from './contact-accordion-section';
import { Controller, useForm } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface EditContactProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  userView?: boolean;
  userRoles: string[];
  contactList: Person[];
  selectedContact: Person;
  selectedLabel: string;
  groupMembers: string[];
  page: number;
  pageSize: number;
  changeLogRoles: string[];
}

export interface EditContactFormData {
  firstName: string;
  lastName: string;
  nickName: string | null;
  birthDay: string | null;
  addresses: PersonAddress[] | null;
  emailAddresses: EmailAddress[] | null;
  organizations: PersonOrg[] | null;
  phoneNumbers: PhoneNumber[] | null;
  relations: ContactRelation[] | null;
  imClients: ImClient[] | null;
  events:
    | {
        date: dayjs.Dayjs;
        type: string;
      }[]
    | null;
  websites: Url[] | null;
  biographies: string | null;
}

export interface FormPerson {
  resourceName: string | null | undefined;
  etag: string;
  addresses: PersonAddress[] | null;
  emailAddresses: EmailAddress[] | null;
  phoneNumbers: PhoneNumber[] | null;
  events: Event[] | null;
  names: ContactName[] | null;
  imClients: ImClient[] | null;
  organizations: PersonOrg[] | null;
  memberships: Membership[];
  urls: Url[] | null;
  birthdays: Birthday[] | null;
  nicknames: NickName[] | null;
  biographies: Biography | null;
  userDefined: UserDefined[] | null;
  relations: ContactRelation[] | null;
  metadata?: PersonMetadata;
}

export default function EditContactDialog({
  open,
  handleClose,
  userEmail,
  userView,
  userRoles,
  contactList,
  selectedLabel,
  groupMembers,
  selectedContact,
  page,
  pageSize,
  changeLogRoles,
}: EditContactProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: FormPerson) =>
      updateContact(userEmail, data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Contact updated successfully!',
      });

      const newList = contactList?.filter(
        (person) => person.resourceName != data.resourceName
      );

      newList?.push(data);

      queryClient.setQueryData(
        [
          UserKeys.Contacts,
          userEmail,
          selectedLabel,
          groupMembers,
          page,
          pageSize,
        ],
        newList
      );
      reset();
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating this contact. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.personal_contacts
          .update_personal_contact,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.personalContacts,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: (data: FormPerson) => updateContactUserPortal(data),
      onSuccess: (data) => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Contact updated successfully!',
        });

        const newList = contactList?.filter(
          (person) => person.resourceName != data.resourceName
        );

        newList?.push(data);

        queryClient.setQueryData(
          [
            UserKeys.Contacts,
            userEmail,
            selectedLabel,
            groupMembers,
            page,
            pageSize,
          ],
          newList
        );
        reset();
        setChangeLogMessage('');
        handleClose();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue updating this contact. Please refresh and try again.',
        });
      },
    });

  function mutateData() {
    const formPerson = {
      ...selectedContact,
      names:
        getValues('firstName').length > 0 &&
        getValues('lastName').length > 0 &&
        selectedContact.names &&
        selectedContact.names[0]
          ? [
              {
                ...selectedContact.names[0],
                displayName: `${getValues('firstName')} ${getValues(
                  'lastName'
                )}`,
                displayNameLastFirst: `${getValues('lastName')}, ${getValues(
                  'firstName'
                )}`,
                unstructuredName: `${getValues('firstName')} ${getValues(
                  'lastName'
                )}`,
                familyName: getValues('lastName'),
                givenName: getValues('firstName'),
              },
            ]
          : getValues('firstName').length > 0 &&
            selectedContact.names &&
            selectedContact.names[0]
          ? [
              {
                ...selectedContact.names[0],
                displayName: getValues('firstName'),
                givenName: getValues('firstName'),
                familyName: '',
                unstructuredName: getValues('firstName'),
              },
            ]
          : getValues('lastName').length > 0 &&
            selectedContact.names &&
            selectedContact.names[0]
          ? [
              {
                ...selectedContact.names[0],
                displayName: getValues('lastName'),
                givenName: getValues('lastName'),
                familyName: '',
                unstructuredName: getValues('lastName'),
              },
            ]
          : selectedContact.names && selectedContact.names[0]
          ? [
              {
                ...selectedContact.names[0],
                displayName: '',
                givenName: '',
                familyName: '',
                unstructuredName: '',
                displayNameLastFirst: '',
              },
            ]
          : null,
      birthdays: selectedContact.birthdays
        ? [
            {
              ...selectedContact.birthdays[0],
              date: {
                day: dayjs(getValues('birthDay')).date(),
                month: dayjs(getValues('birthDay')).month() + 1,
                year: dayjs(getValues('birthDay')).year(),
              },
              text:
                dayjs(getValues('birthDay'))?.toDate().toLocaleDateString() ||
                '',
            },
          ]
        : getValues('birthDay')
        ? [
            {
              date: {
                day: dayjs(getValues('birthDay')).date(),
                month: dayjs(getValues('birthDay')).month() + 1,
                year: dayjs(getValues('birthDay')).year(),
              },
              text:
                dayjs(getValues('birthDay'))?.toDate().toLocaleDateString() ||
                '',
            },
          ]
        : null,
      nicknames: selectedContact.nicknames
        ? [
            {
              ...selectedContact.nicknames[0],
              value: getValues('nickName') || '',
            },
          ]
        : getValues('nickName')
        ? [
            {
              value: getValues('nickName') || '',
              type: 'ALTERNATE_NAME',
            },
          ]
        : null,
      emailAddresses: getValues('emailAddresses')
        ? getValues('emailAddresses')!.map((email) => {
            if (!email.type) {
              return {
                ...email,
                type: '',
              };
            } else {
              return email;
            }
          })
        : null,
      addresses: getValues('addresses')
        ? getValues('addresses')!.map((address) => {
            if (!address.type) {
              return {
                ...address,
                type: '',
              };
            } else {
              return address;
            }
          })
        : null,
      organizations: getValues('organizations')
        ? getValues('organizations')!.map((org) => {
            if (!org.type) {
              return {
                ...org,
                type: '',
              };
            } else {
              return org;
            }
          }) || []
        : null,
      phoneNumbers: getValues('phoneNumbers')
        ? getValues('phoneNumbers')!.map((phoneNumber) => {
            if (!phoneNumber.type) {
              return {
                ...phoneNumber,
                type: '',
              };
            } else {
              return phoneNumber;
            }
          }) || []
        : null,
      urls: getValues('websites')
        ? getValues('websites')!.map((url) => {
            if (!url.type) {
              return {
                ...url,
                type: '',
              };
            } else {
              return url;
            }
          }) || []
        : null,
      imClients: getValues('imClients') ? getValues('imClients') : null,
      relations: getValues('relations') ? getValues('relations') : null,
      biographies: getValues('biographies')
        ? ({
            value: getValues('biographies'),
          } as Biography)
        : null,
      events: getValues('events')
        ? getValues('events')!.map((event) => {
            return {
              type: event.type,
              date: {
                day: event.date.day(),
                month:
                  event.date.month() === 0
                    ? event.date.month() + 1
                    : event.date.month(),
                year: event.date.year(),
              },
            } as Event;
          })
        : null,
    };
    return formPerson as FormPerson;
  }

  const onSubmit = () => {
    if (
      !userView &&
      isAbleToSeeUIElement(changeLogRoles, UserRoles.UpdateContacts.value)
    ) {
      setOpenChangeLogDialog(true);
    } else if (userView) {
      const formPerson = mutateData();
      userPortalMutate(formPerson);
    } else {
      const formPerson = mutateData();
      mutate(formPerson);
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    formState,
    setValue,
    watch,
    getValues,
  } = useForm<EditContactFormData>({
    values: {
      firstName:
        selectedContact.names &&
        selectedContact.names[0] &&
        selectedContact.names[0].givenName
          ? selectedContact.names[0].givenName
          : '',
      lastName:
        selectedContact.names &&
        selectedContact.names[0] &&
        selectedContact.names[0].familyName
          ? selectedContact.names[0].familyName
          : '',
      nickName: selectedContact.nicknames
        ? selectedContact.nicknames[0].value
        : '',
      birthDay:
        selectedContact.birthdays &&
        selectedContact.birthdays[0] &&
        selectedContact.birthdays[0].date
          ? `${selectedContact.birthdays[0].date.month}/${selectedContact.birthdays[0].date.day}/${selectedContact.birthdays[0].date.year}`
          : null,
      events: selectedContact.events
        ? selectedContact.events.map((event) => {
            if (!event.type) {
              return {
                type: 'anniversary',
                date: dayjs(
                  `${event.date.month}/${event.date.day}/${event.date.year}`
                ),
              };
            } else {
              return {
                ...event,
                date: dayjs(
                  `${event.date.month}/${event.date.day}/${event.date.year}`
                ),
              };
            }
          })
        : null,
      addresses: selectedContact.addresses
        ? selectedContact.addresses.map((address) => {
            if (!address.type) {
              return {
                ...address,
                type: 'work',
              };
            } else {
              return address;
            }
          }) || []
        : null,
      emailAddresses: selectedContact.emailAddresses
        ? selectedContact.emailAddresses.map((email) => {
            if (!email.type) {
              return {
                ...email,
                type: 'work',
              };
            } else {
              return email;
            }
          }) || []
        : null,
      organizations: selectedContact.organizations
        ? selectedContact.organizations.map((org) => {
            if (!org.type) {
              return {
                ...org,
                type: 'work',
              };
            } else {
              return org;
            }
          }) || []
        : null,
      phoneNumbers: selectedContact.phoneNumbers
        ? selectedContact.phoneNumbers.map((phoneNumber) => {
            if (!phoneNumber.type) {
              return {
                ...phoneNumber,
                type: 'work',
              };
            } else {
              return phoneNumber;
            }
          }) || []
        : null,
      imClients: selectedContact.imClients
        ? selectedContact.imClients.map((im) => {
            if (!im.type) {
              return {
                ...im,
                type: 'http://schemas.google.com/g/2005#GOOGLETALK',
              };
            } else {
              return im;
            }
          }) || []
        : null,
      relations: selectedContact.relations
        ? selectedContact.relations.map((relation) => {
            if (!relation.type) {
              return {
                ...relation,
                type: 'ASSISTANT',
              };
            } else {
              return relation;
            }
          }) || []
        : null,
      websites: selectedContact.urls
        ? selectedContact.urls.map((url) => {
            if (!url.type) {
              return {
                ...url,
                type: 'work',
              };
            } else {
              return url;
            }
          }) || []
        : null,
      biographies: selectedContact.biographies
        ? selectedContact.biographies[0].value
        : '',
    },
  });

  const { isDirty } = formState;

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        handleClose();
        reset();
      }}
      title={`Edit Contact`}
      maxWidth="md"
      dialogContent={
        <Stack sx={{ p: 2, justifyContent: 'center', mx: 'auto' }}>
          <Stack
            flexDirection="row"
            justifyContent="center"
            gap={6}
            sx={{ p: 2 }}
          >
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoComplete="off"
                  label="First Name"
                  sx={{ width: 300 }}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoComplete="off"
                  label="Last Name"
                  sx={{ minWidth: 300 }}
                />
              )}
            />
          </Stack>
          <Stack
            flexDirection="row"
            justifyContent="center"
            gap={6}
            sx={{ p: 2 }}
          >
            <Controller
              name="nickName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoComplete="off"
                  label="Nickname"
                  sx={{ minWidth: 300 }}
                />
              )}
            />
            <Controller
              name={'birthDay'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  label="Birthday"
                  sx={{ width: 300 }}
                  disableFuture
                  value={value ? dayjs(value) : null}
                  onChange={(e) => {
                    if (e) {
                      onChange(e.toDate());
                    }
                  }}
                />
              )}
            />
          </Stack>
          <Stack width={600} sx={{ mx: 'auto', width: 700 }}>
            <ContactAccordionSection
              control={control}
              controlName="emailAddresses"
              title="Emails"
              selectPropertyOne="type"
              inputProperty="value"
              menuItemsOne={[
                { value: 'home', label: 'Home' },
                { value: 'other', label: 'Other' },
                { value: 'work', label: 'Work' },
                {
                  value: 'http://schemas.google.com/g/2005#home',
                  label: 'Deprecated: Home',
                },
                {
                  value: 'http://schemas.google.com/g/2005#other',
                  label: 'Deprecated: Other',
                },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <ContactAccordionSection
              control={control}
              controlName="addresses"
              title="Addresses"
              selectPropertyOne="type"
              inputProperty="formatted"
              menuItemsOne={[
                { value: 'home', label: 'Home' },
                { value: 'other', label: 'Other' },
                { value: 'work', label: 'Work' },
                {
                  value: 'http://schemas.google.com/g/2005#home',
                  label: 'Deprecated: Home',
                },
                {
                  value: 'http://schemas.google.com/g/2005#other',
                  label: 'Deprecated: Other',
                },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <ContactAccordionSection
              control={control}
              controlName="organizations"
              title="Organizations"
              selectPropertyOne="type"
              inputProperty="value"
              menuItemsOne={[
                { value: 'school', label: 'School' },
                { value: 'work', label: 'Work' },
                {
                  value: 'http://schemas.google.com/g/2005#other',
                  label: 'Deprecated: Other',
                },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <ContactAccordionSection
              control={control}
              controlName="phoneNumbers"
              title="Phones"
              selectPropertyOne="type"
              inputProperty="value"
              menuItemsOne={[
                {
                  value: 'googleVoice',
                  label: 'Google Voice',
                },
                {
                  value: 'home',
                  label: 'Home',
                },
                {
                  value: 'homeFax',
                  label: 'Home Fax',
                },
                {
                  value: 'main',
                  label: 'Main',
                },
                {
                  value: 'mobile',
                  label: 'Mobile',
                },
                {
                  value: 'other',
                  label: 'Other',
                },
                {
                  value: 'otherFax',
                  label: 'Other Fax',
                },
                {
                  value: 'pager',
                  label: 'Pager',
                },
                { value: 'work', label: 'Work' },
                {
                  value: 'workFax',
                  label: 'Work Fax',
                },
                {
                  value: 'workMobile',
                  label: 'Work Mobile',
                },
                {
                  value: 'workPager',
                  label: 'Work Pager',
                },
                {
                  value: 'http://schemas.google.com/g/2005#home',
                  label: 'Deprecated: Home',
                },
                {
                  value: 'http://schemas.google.com/g/2005#home_fax',
                  label: 'Deprecated: Home Fax',
                },
                {
                  value: 'http://schemas.google.com/g/2005#mobile',
                  label: 'Deprecated: Mobile',
                },
                {
                  value: 'http://schemas.google.com/g/2005#other',
                  label: 'Deprecated: Other',
                },
                {
                  value: 'http://schemas.google.com/g/2005#pager',
                  label: 'Deprecated: Pager',
                },
                {
                  value: 'http://schemas.google.com/g/2005#work_fax',
                  label: 'Deprecated: Work Fax',
                },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <ContactAccordionSection
              control={control}
              controlName="relations"
              title="Relations"
              selectPropertyOne="type"
              inputProperty="person"
              menuItemsOne={[
                { value: 'assistant', label: 'Assistant' },
                { value: 'brother', label: 'Brother' },
                { value: 'child', label: 'Child' },
                { value: 'domesticPartner', label: 'Domestic Partner' },
                { value: 'father', label: 'Father' },
                { value: 'friend', label: 'Friend' },
                { value: 'manager', label: 'Manager' },
                { value: 'mother', label: 'Mother' },
                { value: 'parent', label: 'Parent' },
                { value: 'partner', label: 'Partner' },
                { value: 'referredBy', label: 'Referred By' },
                { value: 'relative', label: 'Relative' },
                { value: 'sister', label: 'Sister' },
                { value: 'spouse', label: 'Spouse' },
                { value: 'ASSISTANT', label: 'Deprecated: Assistant' },
                { value: 'BROTHER', label: 'Deprecated: Brother' },
                { value: 'CHILD', label: 'Deprecated: Child' },
                {
                  value: 'DOMESTIC_PARTNER',
                  label: 'Deprecated: Domestic Partner',
                },
                { value: 'FATHER', label: 'Deprecated: Father' },
                { value: 'FRIEND', label: 'Deprecated: Friend' },
                { value: 'MANAGER', label: 'Deprecated: Manager' },
                { value: 'MOTHER', label: 'Deprecated: Mother' },
                { value: 'PARENT', label: 'Deprecated: Parent' },
                { value: 'PARTNER', label: 'Deprecated: Partner' },
                { value: 'REFERRED_BY', label: 'Deprecated: Referred By' },
                { value: 'RELATIVE', label: 'Deprecated: Relative' },
                { value: 'SISTER', label: 'Deprecated: Sister' },
                { value: 'SPOUSE', label: 'Deprecated: Spouse' },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <ContactAccordionSection
              control={control}
              controlName="imClients"
              title="Instant Messaging"
              selectPropertyOne="type"
              inputProperty="username"
              menuItemsOne={[
                { value: 'http://schemas.google.com/g/2005#AIM', label: 'AIM' },
                {
                  value: 'http://schemas.google.com/g/2005#GOOGLETALK',
                  label: 'Google Talk',
                },
                { value: 'http://schemas.google.com/g/2005#ICQ', label: 'ICQ' },
                {
                  value: 'http://schemas.google.com/g/2005#JABBER',
                  label: 'Jabber',
                },
                { value: 'http://schemas.google.com/g/2005#MSN', label: 'MSN' },
                { value: 'http://schemas.google.com/g/2005#QQ', label: 'QQ' },
                {
                  value: 'http://schemas.google.com/g/2005#SKYPE',
                  label: 'Skype',
                },
                {
                  value: 'http://schemas.google.com/g/2005#YAHOO',
                  label: 'Yahoo',
                },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <ContactAccordionSection
              control={control}
              controlName="events"
              title="Events"
              selectPropertyOne="type"
              inputProperty="date"
              menuItemsOne={[
                { value: 'anniversary', label: 'Anniversary' },
                { value: 'other', label: 'Other' },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <ContactAccordionSection
              control={control}
              controlName="websites"
              title="Websites"
              selectPropertyOne="type"
              inputProperty="value"
              menuItemsOne={[
                { value: 'appInstallPage', label: 'App Install Page' },
                { value: 'blog', label: 'Blog' },
                { value: 'ftp', label: 'FTP' },
                { value: 'homePage', label: 'Home Page' },
                { value: 'other', label: 'Other' },
                { value: 'profile', label: 'Profile' },
                { value: 'reservations', label: 'Reservations' },
                { value: 'work', label: 'Work' },
                {
                  value: 'BLOG',
                  label: 'Deprecated: Blog',
                },
                { value: 'HOME_PAGE', label: 'Deprecated: Home Page' },
                { value: 'OTHER', label: 'Deprecated: Other' },
                { value: 'PROFILE', label: 'Deprecated: Profile' },
              ]}
              formState={formState}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
            />
            <Accordion defaultExpanded={selectedContact.biographies !== null}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="notes-content"
                id="notes-header"
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === 'light'
                      ? theme.palette.grey[200]
                      : theme.palette.grey[800],
                }}
              >
                <Typography>Notes</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Controller
                  name="biographies"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      autoComplete="off"
                      multiline
                      rows={3}
                    />
                  )}
                />
              </AccordionDetails>
            </Accordion>
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const formPerson = mutateData();
              mutate(formPerson);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Save',
        action: handleSubmit(onSubmit),
        loading: isPending || userPortalPending,
        disabled:
          !isDirty ||
          (!userView &&
            !isAbleToSeeUIElement(userRoles, UserRoles.UpdateContacts.value)),
      }}
    />
  );
}
